import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@ng-unifi/ui';
import { isNavItemActive } from '../lib/navActive.js';

export interface BottomNavItem {
  key: string;
  label: string;
  /** Shorter label for the cramped bottom bar; falls back to `label`. */
  shortLabel?: string;
  href: string;
  /** Material Symbols icon name. */
  icon?: string;
  badge?: number;
}

interface Props {
  items: BottomNavItem[];
  onMore: () => void;
}

export function BottomNav({ items, onMore }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <nav
      aria-label="Primary"
      className="safe-area-pb fixed bottom-0 left-0 right-0 z-40 flex border-t border-outline-variant bg-surface-container-lowest md:hidden"
    >
      {items.map((item) => {
        const active = isNavItemActive(location.pathname, item.href);
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => navigate(item.href)}
            aria-current={active ? 'page' : undefined}
            // min-w-0: flex-1 sets the basis to 0 but min-width still
            // defaults to auto, so each item refused to shrink below its
            // label. Five of them measured 332px inside a 320px iPhone SE —
            // the primary navigation itself pushed the page sideways.
            className={`relative flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs font-medium ${
              active ? 'text-primary' : 'text-on-surface-variant'
            }`}
          >
            {item.badge && item.badge > 0 ? (
              <span className="absolute right-1/2 top-1.5 flex h-4 min-w-4 translate-x-3 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold text-on-error">
                {item.badge > 9 ? '9+' : item.badge}
              </span>
            ) : null}
            {item.icon ? <Icon name={item.icon} fill={active} className="text-2xl" /> : null}
            <span className="max-w-full truncate leading-none">
              {item.shortLabel ?? item.label}
            </span>
          </button>
        );
      })}
      <button
        type="button"
        onClick={onMore}
        className="flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs font-medium text-on-surface-variant"
      >
        <Icon name="menu" className="text-2xl" />
        <span className="max-w-full truncate leading-none">More</span>
      </button>
    </nav>
  );
}
